/**
 * Profile image helpers.
 *
 * Some user records store only a filename/key (served by our backend image endpoint),
 * while others may already contain a full URL. These helpers normalize both cases.
 */
const IMAGEKIT_ORIGIN = 'https://ik.imagekit.io/8s3jwexmv/';
const IMAGEKIT_TRANSFORMED_FILENAME_REGEX = /-md_[A-Za-z0-9_-]+\.(?:avif|gif|jpe?g|png|webp)$/i;

/**
 * Resolve a profile image value into a displayable URL.
 *
 * Rules:
 * - `null/undefined/empty` -> null
 * - array values -> first entry (legacy shapes)
 * - absolute URLs are returned as-is
 * - otherwise treat the value as an image key and build an ImageKit URL
 *
 * @param {string|string[]} profileImage
 * @returns {string|null}
 */
export const resolveProfileImageUrl = (profileImage) => {
  if (!profileImage) return null;

  const imageValue = Array.isArray(profileImage) ? profileImage[0] : profileImage;
  if (!imageValue || typeof imageValue !== 'string') return null;

  const trimmed = imageValue.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (IMAGEKIT_TRANSFORMED_FILENAME_REGEX.test(trimmed)) {
    return `${IMAGEKIT_ORIGIN}${encodeURIComponent(trimmed)}`;
  }

  return `${IMAGEKIT_ORIGIN}${encodeURIComponent(trimmed)}`;
};

/**
 * Get a single-character initial for avatar placeholders.
 * @param {string} name
 * @returns {string}
 */
export const getProfileInitial = (name) => {
  if (!name || typeof name !== 'string') return '?';
  return name.trim().charAt(0).toUpperCase() || '?';
};
